import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import IntegrationCard from '../Shared/IntegrationCard';
import { AppBar, Box, CircularProgress, Divider, Grid2, Stack, Typography } from '@mui/material';
import POSSelect from '@/components/POS/Common/POSSelect';
import POSAutocomplete from '@/components/POS/Common/POSAutocomplete';
import POSButton from '@/components/POS/Common/POSButton';
import POSHeading from '@/components/POS/Common/POSHeading';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { api } from '@/utils/Api/POS';
import { toast } from 'react-toastify';
import {
    PutApiEconomicConfigBodyCoaConfiguration,
    PutApiEconomicConfigBodyCoaConfigurationCashJournal,
    PutApiEconomicConfigBodyCoaConfigurationCashJournalExpenseCategoriesItem,
} from '@/shared/api/models';

type CoaConfig = PutApiEconomicConfigBodyCoaConfiguration;
type CashJournal = PutApiEconomicConfigBodyCoaConfigurationCashJournal;
type ExpenseCategory = PutApiEconomicConfigBodyCoaConfigurationCashJournalExpenseCategoriesItem;

type EconomicFormValues = {
    coaConfiguration: CoaConfig;
    sendEmail: boolean;
};

type EconomicConfigResponse = {
    success?: boolean;
    data?: {
        coaConfiguration?: Partial<CoaConfig> | null;
        sendEmail?: boolean;
        organizationId?: string;
    } | null;
    coaConfiguration?: Partial<CoaConfig> | null;
    sendEmail?: boolean;
};

type SetupAccount = {
    accountNumber: number;
    accountType: string;
    name: string;
    blockDirectEntries?: boolean;
};

type SetupJournal = {
    journalNumber: number;
    name: string;
};

type SetupAccountingYear = {
    year: string;
};

type SetupVatAccount = {
    vatCode: string;
    name: string;
};

type EconomicSetupData = {
    accountingYears?: SetupAccountingYear[];
    journals?: SetupJournal[];
    accounts?: SetupAccount[];
    vatAccounts?: SetupVatAccount[];
};

type PaymentAccountKey = keyof NonNullable<CashJournal['paymentAccounts']>;
type GeneralAccountKey = keyof NonNullable<CashJournal['general']>;

const SELECTABLE_ACCOUNT_TYPES = new Set(['profitAndLoss', 'status', 'balance']);

const DEFAULT_CASH_JOURNAL: CashJournal = {
    accountingYear: String(new Date().getFullYear()),
    // journalNumber is agreement-specific — resolved from GET /economic/setup-data → journals.
    journalNumber: undefined,
    vatAccount: null,
    salesWithVat: 1010,
    salesWithoutVat: 1030,
    paymentAccounts: {
        cash: 5810,
        card: 5820,
        mobilePay: 5820,
        invoiceBankTransfer: 5820,
        expense: 6835,
        deposit: null,
        giftCard: null,
        cutCard: null,
        onlinePayment: null,
        prepayment: null,
        kort: null,
    },
    general: {
        toBank: 5820,
        transactionFees: 3628,
        cashDifference: null,
    },
    // Populated from GET /api/expense-categories when config loads.
    expenseCategories: [],
};

/** Default coaConfiguration: legacy top-level fields (invoice sync) + cashJournal (kassekladde sync). */
const DEFAULT_COA: CoaConfig = {
    CARD: null,
    CASH: 55040,
    CUT_CARD: null,
    GIFT_CARD: null,
    MOBILE_PAY: null,
    OUTSTANDING: null,
    salesWithVat: 1010,
    BANK_TRANSFER: 55000,
    PRODUCT_GROUP: {
        PRODUCT_WITH_VAT: 1,
        SERVICE_WITH_VAT: 3,
        PRODCUT_WITHOUT_VAT: 2,
        SERVICE_WITHOUT_VAT: 4,
    },
    CUSTOMER_GROUP: 1,
    salesWithoutVat: 1030,
    cashJournal: DEFAULT_CASH_JOURNAL,
};

const DEFAULT_FORM_VALUES: EconomicFormValues = {
    coaConfiguration: DEFAULT_COA,
    sendEmail: true,
};

const PAYMENT_ACCOUNT_FIELDS: Array<{
    key: PaymentAccountKey;
    labelKey: string;
}> = [
    { key: 'cash', labelKey: 'POS.Cash' },
    { key: 'card', labelKey: 'Integration.PaymentCard' },
    { key: 'mobilePay', labelKey: 'Integration.MobilePay' },
    { key: 'invoiceBankTransfer', labelKey: 'Integration.InvoiceBankTransfer' },
    { key: 'expense', labelKey: 'Integration.EconomicExpense' },
    { key: 'deposit', labelKey: 'Integration.EconomicDeposit' },
    { key: 'giftCard', labelKey: 'POS.GiftCard' },
    { key: 'cutCard', labelKey: 'POS.CutCard' },
    { key: 'onlinePayment', labelKey: 'Integration.OnlinePayment' },
    { key: 'prepayment', labelKey: 'Integration.Prepayment' },
    { key: 'kort', labelKey: 'POS.Card' },
];

const GENERAL_ACCOUNT_FIELDS: Array<{
    key: GeneralAccountKey;
    labelKey: string;
}> = [
    { key: 'toBank', labelKey: 'Integration.ToBank' },
    { key: 'transactionFees', labelKey: 'Integration.TransactionFeesBahlouPay' },
    { key: 'cashDifference', labelKey: 'Integration.CashDifference' },
];

function coalesceAccount(value: number | null | undefined, fallback: number | null | undefined): number | null {
    if (value === null || value === undefined) {
        return fallback ?? null;
    }
    return value;
}

function mergeExpenseCategories(
    incoming?: ExpenseCategory[] | null,
    apiCategoryNames: string[] = [],
): ExpenseCategory[] {
    const incomingMap = new Map((incoming ?? []).map((item) => [item.category, item]));
    return apiCategoryNames.map((category) => ({
        category,
        account: incomingMap.get(category)?.account ?? null,
        vatCode: incomingMap.get(category)?.vatCode ?? null,
    }));
}

function mergeCashJournal(incoming?: Partial<CashJournal> | null, apiCategoryNames: string[] = []): CashJournal {
    return {
        accountingYear: incoming?.accountingYear ?? DEFAULT_CASH_JOURNAL.accountingYear,
        journalNumber: incoming?.journalNumber ?? DEFAULT_CASH_JOURNAL.journalNumber,
        vatAccount: incoming?.vatAccount ?? DEFAULT_CASH_JOURNAL.vatAccount,
        salesWithVat: incoming?.salesWithVat ?? DEFAULT_CASH_JOURNAL.salesWithVat,
        salesWithoutVat: incoming?.salesWithoutVat ?? DEFAULT_CASH_JOURNAL.salesWithoutVat,
        paymentAccounts: {
            ...DEFAULT_CASH_JOURNAL.paymentAccounts,
            ...(incoming?.paymentAccounts ?? {}),
        },
        general: {
            ...DEFAULT_CASH_JOURNAL.general,
            ...(incoming?.general ?? {}),
        },
        expenseCategories: mergeExpenseCategories(incoming?.expenseCategories, apiCategoryNames),
    };
}

function mergeCoaConfig(incoming: Partial<CoaConfig> | undefined | null, apiCategoryNames: string[] = []): CoaConfig {
    return {
        CASH: coalesceAccount(incoming?.CASH, DEFAULT_COA.CASH) ?? DEFAULT_COA.CASH,
        CARD: coalesceAccount(incoming?.CARD, DEFAULT_COA.CARD),
        CUT_CARD: coalesceAccount(incoming?.CUT_CARD, DEFAULT_COA.CUT_CARD),
        GIFT_CARD: coalesceAccount(incoming?.GIFT_CARD, DEFAULT_COA.GIFT_CARD),
        MOBILE_PAY: coalesceAccount(incoming?.MOBILE_PAY, DEFAULT_COA.MOBILE_PAY),
        OUTSTANDING: coalesceAccount(incoming?.OUTSTANDING, DEFAULT_COA.OUTSTANDING),
        BANK_TRANSFER: coalesceAccount(incoming?.BANK_TRANSFER, DEFAULT_COA.BANK_TRANSFER) ?? DEFAULT_COA.BANK_TRANSFER,
        salesWithVat: incoming?.salesWithVat ?? DEFAULT_COA.salesWithVat,
        salesWithoutVat: incoming?.salesWithoutVat ?? DEFAULT_COA.salesWithoutVat,
        PRODUCT_GROUP: {
            ...DEFAULT_COA.PRODUCT_GROUP,
            ...(incoming?.PRODUCT_GROUP ?? {}),
        },
        CUSTOMER_GROUP: incoming?.CUSTOMER_GROUP ?? DEFAULT_COA.CUSTOMER_GROUP,
        VAT_ZONE: incoming?.VAT_ZONE,
        PAYMENT_TERMS: incoming?.PAYMENT_TERMS,
        cashJournal: mergeCashJournal(incoming?.cashJournal, apiCategoryNames),
    };
}

function resolveAccountingYear(cashJournal: CashJournal, accountingYears: SetupAccountingYear[]): string | undefined {
    if (cashJournal.accountingYear) {
        return cashJournal.accountingYear;
    }
    const currentYear = String(new Date().getFullYear());
    const matchingYear = accountingYears.find((yearItem) => yearItem.year === currentYear);
    return (
        matchingYear?.year ??
        (accountingYears.length > 0 ? accountingYears[accountingYears.length - 1]?.year : undefined)
    );
}

const EconomicBox: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [setupLoading, setSetupLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasSettings, setHasSettings] = useState(false);
    const [setupData, setSetupData] = useState<EconomicSetupData | null>(null);
    const [initialValues, setInitialValues] = useState<EconomicFormValues>(DEFAULT_FORM_VALUES);
    const [economicAuthUrl, setEconomicAuthUrl] = useState<string | null>(null);
    const hasProcessedTokenRef = useRef(false);
    const expenseCategoryNamesRef = useRef<string[]>([]);

    const resetFormRef = useRef<(nextValues: EconomicFormValues) => void>(() => undefined);

    const fetchAuthUrl = useCallback(async () => {
        try {
            const res: any = await api.getApiAddons();
            const categories = res?.data || res?.data?.data || [];
            const posCategory = Array.isArray(categories) ? categories.find((c: any) => c.categoryId === 1) : null;
            const economicAddon = posCategory?.addons?.find((a: any) => a.id === 2 || a.name === 'economic');
            const url = economicAddon?.meta?.authUrl;
            if (typeof url === 'string' && url.trim()) {
                setEconomicAuthUrl(url);
                return url;
            }
        } catch (err) {
            console.error('Failed to fetch auth url:', err);
        }
        return null;
    }, []);

    const fetchExpenseCategoryNames = useCallback(async (): Promise<string[]> => {
        try {
            const response = await api.getApiExpenseCategories({
                limit: 100,
                sortBy: 'name',
                sortOrder: 'desc',
            });
            return (response?.categories ?? [])
                .map((category) => category.name)
                .filter((name): name is string => Boolean(name));
        } catch (err) {
            console.error('Failed to fetch expense categories:', err);
            return [];
        }
    }, []);

    const fetchSetupData = useCallback(async () => {
        try {
            setSetupLoading(true);
            const response: any = await api.getApiEconomicSetupData();
            const data: EconomicSetupData = response?.data || response;
            setSetupData(data);
            return data;
        } catch {
            setSetupData(null);
            toast.error(t('Integration.FailedToLoadIntegration'));
            return null;
        } finally {
            setSetupLoading(false);
        }
    }, []);

    const applyConfig = useCallback(
        (
            response: EconomicConfigResponse | null,
            setup?: EconomicSetupData | null,
            expenseCategoryNames: string[] = [],
        ) => {
            const coaConfig = response?.data?.coaConfiguration || response?.coaConfiguration;
            const mergedCoa = mergeCoaConfig(coaConfig, expenseCategoryNames);
            const resolvedYear = setup?.accountingYears?.length
                ? resolveAccountingYear(mergedCoa.cashJournal ?? DEFAULT_CASH_JOURNAL, setup.accountingYears)
                : mergedCoa.cashJournal?.accountingYear;

            const sendEmailVal =
                response?.data?.sendEmail !== undefined
                    ? response.data.sendEmail
                    : response?.sendEmail !== undefined
                      ? response.sendEmail
                      : DEFAULT_FORM_VALUES.sendEmail;

            const nextValues: EconomicFormValues = {
                coaConfiguration: {
                    ...mergedCoa,
                    cashJournal: {
                        ...mergedCoa.cashJournal,
                        accountingYear: resolvedYear,
                        journalNumber: mergedCoa.cashJournal?.journalNumber ?? setup?.journals?.[0]?.journalNumber,
                        salesWithVat: mergedCoa.cashJournal?.salesWithVat ?? mergedCoa.salesWithVat,
                        salesWithoutVat: mergedCoa.cashJournal?.salesWithoutVat ?? mergedCoa.salesWithoutVat,
                    },
                },
                sendEmail: sendEmailVal,
            };
            resetFormRef.current(nextValues);
        },
        [],
    );

    const fetchConfig = useCallback(
        async (options?: { showLoading?: boolean }) => {
            const showLoading = options?.showLoading ?? true;
            try {
                if (showLoading) {
                    setLoading(true);
                }
                const [response, setup, expenseCategoryNames] = await Promise.all([
                    api.getApiEconomicConfig() as Promise<any>,
                    fetchSetupData(),
                    fetchExpenseCategoryNames(),
                ]);
                expenseCategoryNamesRef.current = expenseCategoryNames;

                const coaConfig = response?.data?.coaConfiguration || response?.coaConfiguration;
                if (!coaConfig) {
                    applyConfig(null, setup, expenseCategoryNames);
                    setHasSettings(false);
                } else {
                    applyConfig(response, setup, expenseCategoryNames);
                    setHasSettings(true);
                }
            } catch {
                applyConfig(null, null, expenseCategoryNamesRef.current);
                setHasSettings(false);
            } finally {
                if (showLoading) {
                    setLoading(false);
                }
            }
        },
        [applyConfig, fetchExpenseCategoryNames, fetchSetupData],
    );

    const formik = useFormik<EconomicFormValues>({
        initialValues,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setSaving(true);
            try {
                const cashJournal = values.coaConfiguration.cashJournal;
                const coaConfiguration: CoaConfig = {
                    ...values.coaConfiguration,
                    salesWithVat: values.coaConfiguration.salesWithVat ?? DEFAULT_COA.salesWithVat,
                    salesWithoutVat: values.coaConfiguration.salesWithoutVat ?? DEFAULT_COA.salesWithoutVat,
                    cashJournal: {
                        ...cashJournal,
                        journalNumber: cashJournal?.journalNumber ?? setupData?.journals?.[0]?.journalNumber,
                        salesWithVat:
                            cashJournal?.salesWithVat ??
                            values.coaConfiguration.salesWithVat ??
                            DEFAULT_CASH_JOURNAL.salesWithVat,
                        salesWithoutVat:
                            cashJournal?.salesWithoutVat ??
                            values.coaConfiguration.salesWithoutVat ??
                            DEFAULT_CASH_JOURNAL.salesWithoutVat,
                    },
                };
                const response: any = await api.putApiEconomicConfig({
                    sendEmail: values.sendEmail,
                    coaConfiguration,
                });
                toast.success(response?.message || t('Integration.SuccessConfig') || 'Konfiguration opdateret');
                await fetchConfig({ showLoading: false });
            } catch {
                toast.error(t('Integration.FailedConfig') || 'Konfiguration mislykkedes');
            } finally {
                setSaving(false);
            }
        },
    });

    resetFormRef.current = (nextValues: EconomicFormValues) => {
        setInitialValues(nextValues);
        formik.resetForm({ values: nextValues });
    };

    const handleActivate = async () => {
        let authUrl = economicAuthUrl;
        if (!authUrl) {
            try {
                await api.postApiAddonsUpsert({ addonName: 'economic' });
                authUrl = await fetchAuthUrl();
            } catch (err) {
                console.error('Failed to activate economic:', err);
            }
        }
        if (authUrl) {
            window.location.assign(authUrl);
        } else {
            toast.error(t('Integration.FailedToActivate'));
        }
    };

    const handleDisconnect = async () => {
        try {
            setLoading(true);
            await api.postApiEconomicDisconnect();
            setHasSettings(false);
            setSetupData(null);
            resetFormRef.current({
                ...DEFAULT_FORM_VALUES,
                coaConfiguration: mergeCoaConfig(null, expenseCategoryNamesRef.current),
            });
            toast.success(t('Integration.DeactivationSuccess') || 'Integration deaktiveret');
        } catch {
            toast.error(t('Integration.DeactivationFailedMessage') || 'Deaktivering mislykkedes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = searchParams.get('token');

        const initialize = async () => {
            if (token && !hasProcessedTokenRef.current) {
                hasProcessedTokenRef.current = true;
                try {
                    setLoading(true);
                    await api.postApiEconomicConnect({ agreementGrantToken: token });
                    toast.success(t('Integration.EconomicConnectSuccess') || 'E-conomic forbundet med succes');
                    setSearchParams({}, { replace: true });
                } catch {
                    hasProcessedTokenRef.current = false;
                    toast.error(
                        t('Integration.EconomicConnectFailed') ||
                            t('Integration.FailedToActivate') ||
                            'Aktivering mislykkedes',
                    );
                }
            }
            await fetchConfig();
            void fetchAuthUrl();
        };

        void initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const accountingYearOptions = useMemo(() => {
        return (setupData?.accountingYears ?? []).map((yearItem) => ({
            value: yearItem.year,
            label: yearItem.year,
        }));
    }, [setupData?.accountingYears]);

    const journalOptions = useMemo(() => {
        return (setupData?.journals ?? []).map((journal) => ({
            id: journal.journalNumber,
            name: `${journal.journalNumber} - ${journal.name}`,
        }));
    }, [setupData?.journals]);

    const vatOptions = useMemo(() => {
        return (setupData?.vatAccounts ?? []).map((vat) => ({
            id: vat.vatCode,
            name: `${vat.vatCode} - ${vat.name}`,
        }));
    }, [setupData?.vatAccounts]);

    const accountOptions = useMemo(() => {
        return (setupData?.accounts ?? [])
            .filter((account) => SELECTABLE_ACCOUNT_TYPES.has(account.accountType) && !account.blockDirectEntries)
            .map((account) => ({
                id: account.accountNumber,
                name: `${account.accountNumber} - ${account.name}`,
            }));
    }, [setupData?.accounts]);

    const cashJournal = formik.values.coaConfiguration.cashJournal;

    const renderPaymentAccountAutocomplete = (field: PaymentAccountKey) => {
        const selectedValue = cashJournal?.paymentAccounts?.[field];
        const selectedOption =
            accountOptions.find((option) => Number(option.id) === Number(selectedValue)) ||
            (selectedValue !== null && selectedValue !== undefined
                ? { id: selectedValue, name: String(selectedValue) }
                : null);

        return (
            <POSAutocomplete
                options={accountOptions}
                value={selectedOption}
                onChange={(_, value) => {
                    formik.setFieldValue(
                        `coaConfiguration.cashJournal.paymentAccounts.${field}`,
                        value?.id !== undefined && value?.id !== null ? Number(value.id) : null,
                    );
                }}
                getOptionLabel={(option) => option?.name || ''}
                width="100%"
                placeholder={t('GiftCard.SelectOption')}
                sx={{
                    borderRadius: 2,
                    py: 0.5,
                    '.css-bgp2v2-MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
            />
        );
    };

    const renderGeneralAccountAutocomplete = (field: GeneralAccountKey) => {
        const selectedValue = cashJournal?.general?.[field];
        const selectedOption =
            accountOptions.find((option) => Number(option.id) === Number(selectedValue)) ||
            (selectedValue !== null && selectedValue !== undefined
                ? { id: selectedValue, name: String(selectedValue) }
                : null);

        return (
            <POSAutocomplete
                options={accountOptions}
                value={selectedOption}
                onChange={(_, value) => {
                    formik.setFieldValue(
                        `coaConfiguration.cashJournal.general.${field}`,
                        value?.id !== undefined && value?.id !== null ? Number(value.id) : null,
                    );
                }}
                getOptionLabel={(option) => option?.name || ''}
                width="100%"
                placeholder={t('GiftCard.SelectOption')}
                sx={{
                    borderRadius: 2,
                    py: 0.5,
                    '.css-bgp2v2-MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
            />
        );
    };

    const renderJournalAutocomplete = () => {
        const selectedValue = cashJournal?.journalNumber;
        const selectedOption =
            journalOptions.find((option) => Number(option.id) === Number(selectedValue)) ||
            (selectedValue !== null && selectedValue !== undefined
                ? { id: selectedValue, name: String(selectedValue) }
                : null);

        return (
            <POSAutocomplete
                options={journalOptions}
                value={selectedOption}
                onChange={(_, value) => {
                    formik.setFieldValue(
                        'coaConfiguration.cashJournal.journalNumber',
                        value?.id !== undefined && value?.id !== null ? Number(value.id) : null,
                    );
                }}
                getOptionLabel={(option) => option?.name || ''}
                width="100%"
                placeholder={t('GiftCard.SelectOption')}
                sx={{
                    borderRadius: 2,
                    py: 0.5,
                    '.css-bgp2v2-MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
            />
        );
    };

    const renderVatAccountAutocomplete = () => {
        const selectedValue = cashJournal?.vatAccount;
        const selectedOption =
            vatOptions.find((option) => String(option.id) === String(selectedValue)) ||
            (selectedValue !== null && selectedValue !== undefined
                ? { id: selectedValue, name: String(selectedValue) }
                : null);

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    alignItems: { sm: 'center' },
                }}
            >
                <Box sx={{ flex: 1, width: '100%' }}>
                    <POSAutocomplete
                        options={vatOptions}
                        value={selectedOption}
                        onChange={(_, value) => {
                            formik.setFieldValue(
                                'coaConfiguration.cashJournal.vatAccount',
                                value?.id !== undefined && value?.id !== null ? String(value.id) : null,
                            );
                        }}
                        getOptionLabel={(option) => option?.name || ''}
                        width="100%"
                        placeholder={t('GiftCard.SelectOption')}
                        sx={{
                            borderRadius: 2,
                            py: 0.5,
                            '.css-bgp2v2-MuiTypography-root': {
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                            },
                        }}
                    />
                </Box>
                {selectedValue !== null && selectedValue !== undefined && selectedValue !== '' && (
                    <POSButton
                        variant="f_outline"
                        title={t('Integration.RemoveVatAccount') || 'Remove VAT account'}
                        onClick={() => {
                            formik.setFieldValue('coaConfiguration.cashJournal.vatAccount', null);
                        }}
                        width="auto"
                        height={40}
                        sx={{
                            whiteSpace: 'nowrap',
                            px: 2,
                        }}
                    />
                )}
            </Box>
        );
    };

    const renderExpenseAccountAutocomplete = (categoryIndex: number, selectedValue?: number | null) => {
        const selectedOption =
            accountOptions.find((option) => Number(option.id) === Number(selectedValue)) ||
            (selectedValue !== null && selectedValue !== undefined
                ? { id: selectedValue, name: String(selectedValue) }
                : null);

        return (
            <POSAutocomplete
                options={accountOptions}
                value={selectedOption}
                onChange={(_, value) => {
                    formik.setFieldValue(
                        `coaConfiguration.cashJournal.expenseCategories.${categoryIndex}.account`,
                        value?.id !== undefined && value?.id !== null ? Number(value.id) : null,
                    );
                }}
                getOptionLabel={(option) => option?.name || ''}
                width="100%"
                placeholder={t('GiftCard.SelectOption')}
                sx={{
                    borderRadius: 2,
                    py: 0.5,
                    '.css-bgp2v2-MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
            />
        );
    };

    const renderExpenseVatAutocomplete = (categoryIndex: number, selectedValue?: string | number | null) => {
        const selectedOption =
            vatOptions.find((option) => String(option.id) === String(selectedValue)) ||
            (selectedValue !== null && selectedValue !== undefined
                ? { id: selectedValue, name: String(selectedValue) }
                : null);

        return (
            <POSAutocomplete
                options={vatOptions}
                value={selectedOption}
                onChange={(_, value) => {
                    formik.setFieldValue(
                        `coaConfiguration.cashJournal.expenseCategories.${categoryIndex}.vatCode`,
                        value?.id !== undefined && value?.id !== null ? String(value.id) : null,
                    );
                }}
                getOptionLabel={(option) => option?.name || ''}
                width="100%"
                placeholder={t('GiftCard.SelectOption')}
                sx={{
                    borderRadius: 2,
                    py: 0.5,
                    '.css-bgp2v2-MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
            />
        );
    };

    const renderExpenseCategoryLabel = (category: string) => {
        if (category === 'Ikke angivet') {
            return `${category} ${t('Integration.UnspecifiedExpenseCategoryHint')}`;
        }
        return category;
    };

    if (loading && !setupData) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            {formik.dirty && (
                <AppBar
                    sx={{
                        position: 'fixed',
                        top: 45,
                        left: 0,
                        right: 0,
                        py: 1,
                        px: 4,
                        bgcolor: '#fff',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        width: '100%',
                        zIndex: 8,
                        mb: 5,
                    }}
                >
                    <POSButton
                        variant="save"
                        width="auto"
                        height={35}
                        title={saving ? t('Common.Saving') || 'Gemmer...' : t('Setting.SaveChanges')}
                        onClick={() => formik.handleSubmit()}
                        disabled={saving}
                    />
                </AppBar>
            )}

            <Box sx={{ mt: formik.dirty ? 8 : 0 }}>
                {/* Economic Setup / Activation */}
                {!hasSettings ? (
                    <IntegrationCard
                        title={t('Integration.Economic')}
                        description={t('Integration.EconomicDescription')}
                        processContent={
                            <Box>
                                <Typography sx={{ mb: 2, color: '#666', fontSize: '15px' }}>
                                    {t('Integration.EconomicConnectInstructions')}
                                </Typography>
                                <POSButton
                                    title={t('GiftCard.Activate')}
                                    onClick={handleActivate}
                                    width="auto"
                                    variant="save"
                                    height={35}
                                />
                            </Box>
                        }
                    />
                ) : (
                    <Stack sx={{ p: 2, gap: 2 }}>
                        {/* Connected Status Card */}
                        <IntegrationCard
                            infoContent={
                                <Box>
                                    <POSHeading text={t('Integration.EconomicConnectTitle')} />
                                    <Typography sx={{ color: '#666', fontSize: '15px', mt: 0.5 }}>
                                        {t('Integration.EconomicDescription')}
                                    </Typography>
                                </Box>
                            }
                            processContent={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor: '#10b981',
                                            }}
                                        />
                                        <Typography sx={{ fontSize: 14, color: '#065f46', fontWeight: 600 }}>
                                            {t('Integration.EconomicConnected')}
                                        </Typography>
                                    </Box>
                                    <POSButton
                                        variant="delete"
                                        title={t('Integration.GoogleCalendarDisconnect')}
                                        onClick={handleDisconnect}
                                        width="auto"
                                        height={36}
                                        disabled={loading || saving}
                                    />
                                </Box>
                            }
                        />

                        <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                        {/* Financial Year */}
                        <IntegrationCard
                            title={t('Integration.FinancialYear')}
                            description={t('Integration.EconomicFinancialYearDescription')}
                            processContent={
                                <Box sx={{ maxWidth: { xs: '100%', md: 450 } }}>
                                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            {t('Integration.FinancialYear')}
                                        </Typography>
                                        {(loading || setupLoading) && <CircularProgress size={16} />}
                                    </Box>
                                    <POSSelect
                                        options={accountingYearOptions}
                                        value={cashJournal?.accountingYear ?? ''}
                                        onChange={(e) => {
                                            formik.setFieldValue(
                                                'coaConfiguration.cashJournal.accountingYear',
                                                e.target.value as string,
                                            );
                                        }}
                                    />
                                </Box>
                            }
                        />

                        <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                        {/* Cash Journal */}
                        <IntegrationCard
                            title={t('Integration.CashJournal')}
                            description={t('Integration.CashJournalDescription')}
                            processContent={
                                <Box sx={{ maxWidth: { xs: '100%', md: 450 } }}>
                                    <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                        {t('Integration.CashJournal')}
                                    </Typography>
                                    {renderJournalAutocomplete()}
                                </Box>
                            }
                        />

                        <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                        {/* VAT Account */}
                        <IntegrationCard
                            title={t('Integration.VatAccount')}
                            description={t('Integration.EconomicVatAccountDescription')}
                            processContent={
                                <Box sx={{ maxWidth: { xs: '100%', md: 550 } }}>
                                    <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                        {t('Integration.VatAccount')}
                                    </Typography>
                                    {renderVatAccountAutocomplete()}
                                </Box>
                            }
                        />

                        <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                        {/* Payment Mapping */}
                        <IntegrationCard
                            title={t('POS.PaymentMethods')}
                            description={t('Integration.EconomicPaymentMethodsDescription')}
                            processContent={
                                <Grid2 container spacing={3}>
                                    {PAYMENT_ACCOUNT_FIELDS.map((field) => (
                                        <Grid2 key={field.key} size={{ xs: 12, md: 6 }}>
                                            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                                {t(field.labelKey)}
                                            </Typography>
                                            {renderPaymentAccountAutocomplete(field.key)}
                                        </Grid2>
                                    ))}
                                </Grid2>
                            }
                        />

                        <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                        {/* General Settings */}
                        <IntegrationCard
                            title={t('Integration.General')}
                            description={t('Integration.GeneralDescription')}
                            processContent={
                                <Grid2 container spacing={3}>
                                    {GENERAL_ACCOUNT_FIELDS.map((field) => (
                                        <Grid2 key={field.key} size={{ xs: 12, md: 6 }}>
                                            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>
                                                {t(field.labelKey)}
                                            </Typography>
                                            {renderGeneralAccountAutocomplete(field.key)}
                                        </Grid2>
                                    ))}
                                </Grid2>
                            }
                        />

                        <Divider sx={{ border: '2.7px solid #D9D9D9', backgroundColor: '#F3F3F3', width: '100%' }} />

                        {/* Expense Categories */}
                        <IntegrationCard
                            title={t('Integration.ExpenseCategories')}
                            description={t('Integration.ExpenseCategoriesDescription')}
                            processContent={
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    {(cashJournal?.expenseCategories ?? []).map((categoryItem, categoryIndex) => (
                                        <Box
                                            key={categoryItem.category}
                                            sx={{
                                                pb: 2.5,
                                                mb: 2.5,
                                                borderBottom:
                                                    categoryIndex < (cashJournal?.expenseCategories?.length ?? 0) - 1
                                                        ? '1px solid #f0f0f0'
                                                        : 'none',
                                            }}
                                        >
                                            <Typography
                                                sx={{ fontWeight: 'bold', fontSize: '15px', mb: 1.5, color: '#333' }}
                                            >
                                                {renderExpenseCategoryLabel(categoryItem.category)}
                                            </Typography>
                                            <Grid2 container spacing={3}>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Typography
                                                        sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#555' }}
                                                    >
                                                        {t('POS.Account')}
                                                    </Typography>
                                                    {renderExpenseAccountAutocomplete(
                                                        categoryIndex,
                                                        categoryItem.account,
                                                    )}
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Typography
                                                        sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#555' }}
                                                    >
                                                        {t('Integration.VatCode')}
                                                    </Typography>
                                                    {renderExpenseVatAutocomplete(categoryIndex, categoryItem.vatCode)}
                                                </Grid2>
                                            </Grid2>
                                        </Box>
                                    ))}
                                </Box>
                            }
                        />
                    </Stack>
                )}
            </Box>
        </>
    );
};

export default EconomicBox;
